import { type ReactNode } from 'react';

import { History, Info } from 'lucide-react';

import { formatarDataHora } from '@/nucleo/formato/datas';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';

export interface SummaryItem {
  label: string;
  value: ReactNode;
}

interface DetailSummarySidebarProps {
  /** Título do bloco (default: "Resumo"). */
  title?: string;
  /** Itens do resumo (label + valor). Itens com `value` falsy/nulo são filtrados. */
  items: Array<SummaryItem | null | false | undefined>;
  /** Datas de auditoria. Aceita `Date`, string ISO ou nulo/indefinido. */
  audit?: {
    createdAt?: string | Date | null;
    updatedAt?: string | Date | null;
    createdBy?: string | null;
    updatedBy?: string | null;
  };
  /** Slot extra abaixo da auditoria (ex.: ações rápidas). */
  extra?: ReactNode;
}

function dataHoraOuNulo(valor: string | Date | null | undefined): string | null {
  if (!valor) {
    return null;
  }
  const formatado = formatarDataHora(valor);
  return formatado === '—' ? null : formatado;
}

/**
 * Card lateral padrão para telas de detalhe de cadastros (Credenciado,
 * Condutor, Monitor, Veículo, Titular). Pensado para ser usado como
 * `timeline` de `<DetailWithTimeline>`, ocupando a coluna direita sticky no
 * desktop.
 *
 * Portado do protótipo (`components/layout/DetailSummarySidebar.tsx`), com a
 * formatação de data trocada para `formatarDataHora` (nucleo/formato/datas.ts)
 * em vez de `date-fns` direto — mesmo helper que o resto do sistema usa.
 *
 * - Mostra um bloco de "Resumo" (id, status, vínculos, contadores, etc.).
 * - Mostra metadados de auditoria (criado/atualizado em + por quem).
 * - Aceita slot `extra` para ações contextuais.
 */
export function DetailSummarySidebar({
  title = 'Resumo',
  items,
  audit,
  extra,
}: DetailSummarySidebarProps) {
  const itensValidos = items.filter(
    (item): item is SummaryItem =>
      !!item && item.value !== null && item.value !== undefined && item.value !== '',
  );

  const criadoEm = dataHoraOuNulo(audit?.createdAt);
  const atualizadoEm = dataHoraOuNulo(audit?.updatedAt);
  const temAuditoria = criadoEm || atualizadoEm || audit?.createdBy || audit?.updatedBy;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Info className="text-muted-foreground h-4 w-4" aria-hidden="true" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {itensValidos.length > 0 && (
          <dl className="space-y-3">
            {itensValidos.map((item) => (
              <div key={item.label}>
                <dt className="text-muted-foreground text-xs">{item.label}</dt>
                <dd className="mt-0.5 text-sm font-medium wrap-break-word">{item.value}</dd>
              </div>
            ))}
          </dl>
        )}

        {temAuditoria && (
          <div className="space-y-2 border-t pt-3">
            <p className="text-muted-foreground flex items-center gap-1.5 text-xs font-semibold">
              <History className="h-3 w-3" aria-hidden="true" /> Auditoria
            </p>
            {criadoEm && (
              <div>
                <dt className="text-muted-foreground text-xs">Criado em</dt>
                <dd className="text-xs">{criadoEm}</dd>
                {audit?.createdBy && (
                  <dd className="text-muted-foreground text-xs">por {audit.createdBy}</dd>
                )}
              </div>
            )}
            {atualizadoEm && atualizadoEm !== criadoEm && (
              <div>
                <dt className="text-muted-foreground text-xs">Atualizado em</dt>
                <dd className="text-xs">{atualizadoEm}</dd>
                {audit?.updatedBy && (
                  <dd className="text-muted-foreground text-xs">por {audit.updatedBy}</dd>
                )}
              </div>
            )}
          </div>
        )}

        {extra && <div className="border-t pt-3">{extra}</div>}
      </CardContent>
    </Card>
  );
}
